import axios from 'axios';
import store from '@/store/store';
import {
    SET_COMPONENT_DATA,
} from '@/store/types/mutation-types';

const isDev = (process.env.VUE_APP_ENV === 'development');
const isPublic = (process.env.VUE_APP_ENV === 'public');
let sampleComponentData;
let sampleComponentMenuData;
let sampleContentNavData;
let sampleItemDataContent;
let sampleItemDataMedia;
let sampleJssData;
let sampleLanguagesData;
let sampleMediaData;
let samplePremadeComponent;
let samplePremadeComponentsList;
let samplePresetData;
let samplePrototypeData;
let sampleTemplateData;

const SC_API_KEY = process.env.VUE_APP_JSS_API_KEY;
const API_CM = process.env.VUE_APP_CM_URL || 'http://flex91.xcentium.com';

// requires relative path if API is needed from a razor view
let API_FLEX_ELEMENTS = '/sitecore/api/ssc/flex/FlexElements/1/';
let API_FLEX_PUBLIC = '/sitecore/api/ssc/flex/FlexPublic/1/';
let API_JSS = '/sitecore/api/layout/render/flex-jss';
if (!isPublic) {
    API_FLEX_ELEMENTS = `${ API_CM }${ API_FLEX_ELEMENTS }`;
    API_JSS = `${ API_CM }${ API_JSS }`;
    API_FLEX_PUBLIC = `${ API_CM }${ API_FLEX_PUBLIC }`;
}

const API_CREATE_PAGE = 'CreatePage';
const API_CREATE_MEDIA_FOLDER = 'CreateMediaFolder';
const API_CREATE_FOLDER = 'CreateFolder';
const API_CREATE_COMPONENT_CATEGORY = 'CreateComponentCategory';
const API_UPLOAD_MEDIA = 'UploadMedia';
const API_CREATE_TEMPLATE = 'CreateTemplate';
const API_DELETE_COMPONENT = 'DeleteComponent';
const API_EXECUTE_WORKFLOW = 'ExecuteWorkflow';
const API_GET_CHILDREN = 'GetChildren';
const apiGetFields = (itemid, fieldtype) => `GetFields?itemid=${ itemid }&fieldtype=${ fieldtype }`;
const apiGetComponent = id => `GetComponent?id=${ id }`;
const API_GET_COMPONENT_MENU = 'GetComponentMenu';
const API_GET_CONTENT_ITEM = 'GetContentItem';
const API_GET_CONTENT_TREE = 'GetContentTree';
const API_GET_LANGUAGES = 'GetLanguages';
const API_GET_MEDIA_ITEM = 'GetMediaItem';
const API_GET_MEDIA_TREE = 'GetMediaTree';
const API_GET_SETTINGS = 'LoadSettings'; /* eslint-disable-line no-unused-vars */
const apiGetPremadeComponent = id => `GetPremadeComponent?id=${ id }`;
const API_GET_PREMADE_COMPONENTS_LIST = 'GetPremadeComponentsList';
const API_GET_PRESETS = 'GetPresets';
const API_GET_USERNAME = 'GetUsername';
const apiGetNextSteps = itemid => `GetNextSteps?itemid=${ itemid }`;
const API_MOVE_COMPONENT = 'MoveComponent';
const API_POLL_STATUS = 'PollStatus';
const API_PUBLISH_ITEM = 'PublishItem';
const API_SAVE_PAGE = 'SavePage';
const API_SAVE_TEMPLATE = 'SavePageTemplate';
const API_SAVE_COMPONENT = 'SaveComponent';
const API_SAVE_COMPONENT_NAME = 'SaveComponentName';
const API_SAVE_SETTINGS = 'SaveSettings';
const apiToggleLock = itemid => `ToggleLock?itemid=${ itemid }`;
const API_CREATE_COMPONENT = 'CreateComponent';
const API_DELETE_PAGE = 'DeleteItem';
const API_CREATE_TAXONOMY_TAG = 'CreateTaxonomyTag';

const API_GET_TEMPLATES = 'getTemplates';
const apiGetTemplate = id => `getTemplate?id=${ id }`;
const API_GET_PROTOTYPES = 'getPrototypes';
const apiGetPrototype = id => `getPrototype?id=${ id }`;

const jssApi = axios.create({
    baseURL: API_JSS,
    params: {
        sc_apikey: SC_API_KEY,
        database: 'master',
    },
});

export const flexApi = axios.create({
    baseURL: API_FLEX_ELEMENTS,
    params: {
        // api key is authorization
        sc_apikey: SC_API_KEY,
    },
});

export const flexApiPublic = axios.create({
    baseURL: API_FLEX_PUBLIC,
    params: {
        // api key is authorization
        sc_apikey: SC_API_KEY,
    },
});

// Status constants
export const SUCCESS = 'success';
export const ERROR = 'error';

export class Response {
    constructor(status, data, error) {
        this.status = status;
        // data is null if error
        this.data = data;
        // error message
        this.error = error;
    }
}

/**
 * Handle error from Sitecore response
 * @param {Error} error Error from response
 */
export function handleHttpError(error) {
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        return new Response(ERROR, undefined, `Server error: ${ error.response.status }`);
    } if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        return new Response(ERROR, undefined, 'No response from server.');
    }
    // Something happened in setting up the request that triggered an Error
    return new Response(ERROR, undefined, error.message);
}

/**
 * Polls status and makes sure token is valid
 * @returns {Response} Reponse with workflow steps
 */
export async function pollStatus() {
    if (isDev) return Promise.resolve(new Response(SUCCESS, { data: { data: true } }));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.get(API_POLL_STATUS, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Content

/**
 * Get available fields from an item for value linking
 * @param {string} itemid item ID
 * @param {string} fieldtype sitecore field type
 * @returns {Response} Reponse with status, success data is the available fields
 */
export async function getAvailableFields(itemid, fieldtype) {
    if (isDev) return Promise.resolve(new Response(sampleContentNavData.status, sampleContentNavData.data));

    try {
        const response = await flexApi.get(apiGetFields(itemid, fieldtype));
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Get content tree starting from Home
 * @returns {Response} Reponse with status, success data is the content tree
 */
export async function getContentTree() {
    if (isDev) return Promise.resolve(new Response(sampleContentNavData.status, sampleContentNavData.data));

    try {
        const response = await flexApi.get(API_GET_CONTENT_TREE);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Get children starting from path or id
 * @returns {Response} Reponse with status, success data is item data with children
 */
export async function getChildren(path) {
    if (isDev) return Promise.resolve(new Response(sampleContentNavData.status, sampleContentNavData.data));

    try {
        const response = await flexApi.get(API_GET_CHILDREN, {
            params: { path },
        });
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Get content item data
 * @param {string} id item ID
 * @returns {Response} Reponse with status, success data is item data
 */
export async function getContentItem(id) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, sampleItemDataContent.data.data));

    try {
        const response = await flexApi.get(API_GET_CONTENT_ITEM, {
            params: { id },
        });
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Content end

// Media

/**
 * Get media tree starting from Project folder
 * @param {string} filter media type to filter by (image, audio, video, document, file)
 * @returns {Response} Reponse with status, success data is the media tree
 */
export async function getMediaTree(filter = '') {
    if (isDev) return Promise.resolve(new Response(sampleMediaData.status, sampleMediaData.data.data));

    try {
        const response = await flexApi.get(API_GET_MEDIA_TREE, {
            params: { filter },
        });
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Get media item data
 * @param {string} id item ID
 * @returns {Response} Reponse with status, success data is item data
 */
export async function getMediaItem(id) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, sampleItemDataMedia.data.data));

    try {
        const response = await flexApi.get(API_GET_MEDIA_ITEM, {
            params: { id },
        });
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Media end

// Page

/**
 * Create page data by ID
 * @param {PageData} pageData Page data/metadata
 */
export async function createPage(pageData) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'f3871de7-0d81-4186-a8cd-385fe78d9a54'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_CREATE_PAGE, pageData, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        // @TODO - log this error in the DB
        // may need another logging method, if this fails, DB not accessible
        return handleHttpError(error);
    }
}

export function getMediaUploadParameters() {
    return {
        headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` },
        postAction: `${ API_FLEX_ELEMENTS }${ API_UPLOAD_MEDIA }`,
        params: { sc_apikey: SC_API_KEY },
    };
}

export async function createMediaFolder(data) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'f3871de7-0d81-4186-a8cd-385fe78d9a54'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_CREATE_MEDIA_FOLDER, data, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        // @TODO - log this error in the DB
        // may need another logging method, if this fails, DB not accessible
        return handleHttpError(error);
    }
}

export async function createFolder(data) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'f3871de7-0d81-4186-a8cd-385fe78d9a54'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_CREATE_FOLDER, data, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        // @TODO - log this error in the DB
        // may need another logging method, if this fails, DB not accessible
        return handleHttpError(error);
    }
}

/**
 * Create template data by name
 * @param {PageData} pageData Page data/metadata
 */
export async function createTemplate(pageData) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'f3871de7-0d81-4186-a8cd-385fe78d9a54'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_CREATE_TEMPLATE, pageData, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        // @TODO - log this error in the DB
        // may need another logging method, if this fails, DB not accessible
        return handleHttpError(error);
    }
}

/**
 * Get page data by ID
 * @param {string} id The page ID
 */
export async function getPage(id, lang) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, sampleJssData));

    try {
        const response = await jssApi.get('', {
            params: {
                item: id,
                sc_lang: lang,
            },
        });

        return new Response(SUCCESS, response.data);
    } catch (error) {
        // @TODO - log this error in the DB
        // may need another logging method, if this fails, DB not accessible
        return handleHttpError(error);
    }
}

/**
 * Post updated page data to Sitecore
 * @param {SitecorePage} page Entire page data, layout and metadata
 * @returns {Response} Reponse with status, success data is ID of created/updated item
 */
export async function postPage(page) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully posted page.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_SAVE_PAGE, page, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Post updated page template to Sitecore
 * @param {SitecorePage} template Entire template data, layout and metadata
 * @returns {Response} Reponse with status, success data is ID of created/updated item
 */
export async function postTemplate(template) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully posted template.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_SAVE_TEMPLATE, template, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Delete page in Sitecore
 * @param {string} deleteItemPathOrId page or id of the page item to delete
 * @param {number} deleteOption page or id of the page item
 * @param {string} relinkItemPathOrId page or id of the page item
 * @returns {Response} Reponse with status
 */
export async function deletePage(deleteItemPathOrId, deleteOption, relinkItemPathOrId) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, ''));

    try {
        const response = await flexApi.delete(API_DELETE_PAGE, {
            params: { deleteItemPathOrId, deleteOption, relinkItemPathOrId },
            headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` },
        });
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Page end

// Presets

/**
 * Get all user-defined presets
 * @returns {Response} Reponse with status, success data is object with element presets
 */
export async function getPresets() {
    if (isDev) return Promise.resolve(new Response(samplePresetData.status, samplePresetData.data));

    try {
        const response = await flexApi.get(API_GET_PRESETS);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Presets end

/**
 * Post updated settings to Sitecore
 * @param {TBD} settings The user-defined settings
 * @returns {Response} Reponse with status
 */
export async function postSettings(settings) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully updated the settings.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_SAVE_SETTINGS, settings, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Settings end

/**
 * Load settings to Sitecore
 * @param {TBD} settings The user-defined settings
 * @returns {Response} Reponse with status
 */

export async function loadSettings() {
    if (isDev) return Promise.resolve(new Response(samplePresetData.status, samplePresetData.data));

    try {
        const response = await flexApi.get(API_GET_SETTINGS);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        const loadSettingsData = response.data.data;
        localStorage.setItem('isPubTargetAvail', loadSettingsData.publishingTarget.isPublishTargetsAvail);

        const publishingTargetsJson = JSON.stringify(loadSettingsData.publishingTarget.publishTargets);
        localStorage.setItem('publishingTargets', publishingTargetsJson);

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Settings end


// Templates

/**
 * Get all templates from Sitecore
 * @returns {Response} Reponse with status, success data is array of templates
 */
export async function getTemplates() {
    const config = {
        headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` },
    };
    if (isDev) return Promise.resolve(new Response(sampleTemplateData.status, sampleTemplateData.data));

    try {
        const response = await flexApi.get(API_GET_TEMPLATES, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Get template by ID from Sitecore
 * @param {string} id Template ID
 * @returns {Response} Reponse with status, success data is template
 */
export async function getTemplate(id) {
    if (isDev) return Promise.resolve(new Response(sampleTemplateData.status, sampleTemplateData.data.find(t => t.id === id)));

    try {
        const response = await flexApi.get(apiGetTemplate(id));
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Templates end

// Prototypes

/**
 * Get all prototypes from Sitecore
 * @returns {Response} Reponse with status, success data is array of prototypes
 */
export async function getPrototypes() {
    if (isDev) return Promise.resolve(new Response(samplePrototypeData.status, samplePrototypeData.data));

    try {
        const response = await flexApi.get(API_GET_PROTOTYPES);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Get prototype by ID from Sitecore
 * @param {string} id Prototype ID
 * @returns {Response} Reponse with status, success data is prototype
 */
export async function getPrototype(id) {
    if (isDev) return Promise.resolve(new Response(samplePrototypeData.status, samplePrototypeData.data.find(t => t.id === id)));

    try {
        const response = await flexApi.get(apiGetPrototype(id));
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Prototypes end

// Components and Prototypes

/**
* Get a component by ID from Sitecore
* @param {string} id Component ID
* @returns {Response} Reponse with status, success data is the component
 */
export async function getComponent(id, isPublicUrl) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, sampleComponentData));

    try {
        // check vuex cache
        const index = store.state.components.findIndex(i => i.id === id);

        if (index > -1) {
            return new Response(SUCCESS, store.state.components[index]);
        }

        const response = isPublicUrl
            ? await flexApiPublic.get(apiGetComponent(id))
            : await flexApi.get(apiGetComponent(id));

        if (response.data.status === ERROR) {
            return Promise.resolve(new Response(ERROR, response.error));
        }

        store.commit(SET_COMPONENT_DATA, response.data.data);

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

export async function getComponentPublic(id) {
    return getComponent(id, true);
}

/**
 * Post new rendering data to Sitecore
 * @param {SitecoreRendering} rendering Entire rendering data
 * @returns {Response} Reponse with status, success data is ID of created/updated item
 */
export async function createComponent(rendering) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully posted the rendering.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_CREATE_COMPONENT, rendering, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Post updated rendering data to Sitecore
 * @param {SitecoreRendering} rendering Entire rendering data
 * @returns {Response} Reponse with status, success data is ID of created/updated item
 */
export async function saveComponent(rendering) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully posted the rendering.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_SAVE_COMPONENT, rendering, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Post updated rendering display name to Sitecore
 * @param {string} itemId The ID of the menu item to update
 * @param {string} displayName The new display name of the component
 * @returns {Response} Reponse with status, success data is ID of updated item
 */
export async function saveComponentName(itemId, displayName) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully updated the rendering.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_SAVE_COMPONENT_NAME, {
            itemId,
            editorName: displayName,
        }, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Move component
 * @param {string} menuItemId The ID of the menu item to move
 * @param {string} targetMenuItemId The ID of the target menu item to move to
 * @returns {Response} Reponse with status
 */
export async function moveComponentMenu(menuItemId, targetMenuItemId) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, ''));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_MOVE_COMPONENT, {
            menuItemId,
            targetMenuItemId,
        }, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Create component category or cubcategory
 * @param {string} parentItemId The ID of where to create a category under
 * @param {string} name The name of the category
 * @returns {Response} Reponse with status
 */
export async function createComponentCategory(parentItemId, name) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, ''));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_CREATE_COMPONENT_CATEGORY, {
            path: parentItemId,
            name,
        }, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Delete component from Sitecore
 * @param {string} itemId The ID of the component to delete
 * @param {string} renderingId The ID of the rendering to delete
 * @returns {Response} Reponse with status
 */
export async function deleteComponent(itemId, renderingId) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully deleted the rendering.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_DELETE_COMPONENT, {
            itemId,
            renderingId,
        }, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

export async function getComponentLibrary() {
    if (isDev) return Promise.resolve(new Response(SUCCESS, sampleComponentMenuData));

    try {
        const response = await flexApi.get(API_GET_COMPONENT_MENU);

        if (response.data.status === ERROR) {
            return Promise.resolve(new Response(ERROR, response.error));
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

export async function getPremadeComponentsList() {
    if (isDev) return Promise.resolve(new Response(SUCCESS, samplePremadeComponentsList.database));

    try {
        const response = await flexApi.get(API_GET_PREMADE_COMPONENTS_LIST);

        if (response.data.status === ERROR) {
            return Promise.resolve(new Response(ERROR, response.error));
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

export async function getPremadeComponent(id) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, samplePremadeComponent.data));

    try {
        const response = await flexApi.get(apiGetPremadeComponent(id));

        if (response.data.status === ERROR) {
            return Promise.resolve(new Response(ERROR, response.error));
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Components and Prototypes end

// Languages

export async function getLanguages() {
    if (isDev) return Promise.resolve(new Response(SUCCESS, sampleLanguagesData.data));

    try {
        const response = await flexApi.get(API_GET_LANGUAGES);

        if (response.data.status === ERROR) {
            return Promise.resolve(new Response(ERROR, response.error));
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

// Languages end

// Security and workflow

/**
 * Gets Sitecore username with domain for authenticated user
 * @returns {Response} Reponse with username
 */
export async function getUsername() {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'sitecore\\Admin'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.get(API_GET_USERNAME, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Gets next possible workflow steps for current user
 * @param {string} itemId The ID of the page to get workflow commands for
 * @returns {Response} Reponse with workflow steps
 */
export async function getWorkflowSteps(itemId) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, [{ name: 'Approve', id: '00000000-0000-0000-0000-000000000000' }]));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.get(apiGetNextSteps(itemId), config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Executes a workflow command
 * @param {string} itemId The ID of the page to execute command on
 * @param {string} commandId The commandID to execute
 * @param {string} comment The workflow comment
 * @returns {Response} Reponse with status
 */
export async function executeWorkflow(itemId, commandId, comment) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, { state: 'draft', locked: false, lockedby: 'sitecore/Admin' }));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_EXECUTE_WORKFLOW, { itemId, commandId, comment }, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Publishes a page and its data directory
 * @param {string} itemId The ID of the page to publish
 * @param {string} language Item language to publish
 * @returns {Response} Reponse with status
 */
export async function publishItem(itemId, language, publishingTargets) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, { state: 'draft', locked: false, lockedby: 'sitecore/Admin' }));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_PUBLISH_ITEM, { itemId, language, publishingTargets }, config);

        if (response.data.status === ERROR) {
            console.log('ERROR in publishItem');
            return new Response(ERROR, response.data.data, response.data.error);
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}

/**
 * Toggle lock status on an item
 * @param {string} itemid The ID of the page to lock or unlock
 * @returns {Response} Reponse with status
 */
export async function toggleLock(itemid) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'Successfully lock toggled.'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(apiToggleLock(itemid), {}, config);

        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }

        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        return handleHttpError(error);
    }
}
export async function createTaxonomyTag(name, parentId, templateId) {
    if (isDev) return Promise.resolve(new Response(SUCCESS, 'f3871de7-0d81-4186-a8cd-385fe78d9a54'));

    try {
        const config = { headers: { Authorization: `Bearer ${ store.state.user.user.access_token }` } };
        const response = await flexApi.post(API_CREATE_TAXONOMY_TAG, { name, parentId, templateId }, config);
        if (response.data.status === ERROR) {
            return new Response(ERROR, response.data.data, response.data.error);
        }
        return new Response(SUCCESS, response.data.data);
    } catch (error) {
        // @TODO - log this error in the DB
        // may need another logging method, if this fails, DB not accessible
        return handleHttpError(error);
    }
}
// Security and workflow end
